import { useEffect, useMemo, useState } from "react";
import { Copy, RotateCcw, Sparkles } from "lucide-react";
import { getWorkspaceCopy } from "../data/i18n";
import { getLocale, locales } from "../data/locales";
import { transformText } from "../lib/aiClient";
import { analyzeText } from "../lib/textMetrics";
import type { AiTransformResponse, Locale, ToolDefinition } from "../types";

type ToolWorkspaceProps = {
  tool: ToolDefinition;
  locale: Locale;
};

const SAMPLE_TEXT =
  "Our platform leverages innovative solutions to enhance productivity. It is designed to help teams work better and achieve more with less effort.";

const DAILY_AI_LIMIT = 8;
const HISTORY_KEY = "textpolish-history-v1";
const MAX_IMPORTED_CHARS = 8000;

type HistoryItem = {
  id: string;
  toolId: string;
  toolName: string;
  mode: string;
  sourceLanguage: string;
  targetLanguage: string;
  scenario: string;
  input: string;
  output: string;
  createdAt: string;
};

const scenarioTemplates = [
  {
    id: "general",
    labelKey: "scenarioGeneral",
    sample: SAMPLE_TEXT
  },
  {
    id: "email",
    labelKey: "scenarioEmail",
    sample:
      "Hi team, I wanted to follow up on the launch plan. The current draft has the main points, but it needs to sound clearer, more respectful, and easier to act on."
  },
  {
    id: "academic",
    labelKey: "scenarioAcademic",
    sample:
      "This paper examines how remote collaboration affects team productivity. The discussion should be precise, properly qualified, and suitable for an academic reader."
  },
  {
    id: "linkedin",
    labelKey: "scenarioLinkedIn",
    sample:
      "I learned a lot from building this product. The biggest lesson is that users do not need more features; they need workflows that fit how they already work."
  },
  {
    id: "support",
    labelKey: "scenarioSupport",
    sample:
      "Thanks for reaching out. I understand this has been frustrating. Here is what happened, what we can do now, and how we will prevent it next time."
  },
  {
    id: "product",
    labelKey: "scenarioProduct",
    sample:
      "TextPolish helps busy teams turn rough notes into clear, audience-ready writing without losing the original message."
  }
] as const;

function todayUsageKey() {
  return `textpolish-usage-${new Date().toISOString().slice(0, 10)}`;
}

function createId() {
  return typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
}

function readStoredHistory(): HistoryItem[] {
  try {
    const raw = window.localStorage.getItem(HISTORY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.slice(0, 8) : [];
  } catch {
    return [];
  }
}

function readUsageCount() {
  try {
    return Number(window.localStorage.getItem(todayUsageKey()) || "0");
  } catch {
    return 0;
  }
}

export function ToolWorkspace({ tool, locale }: ToolWorkspaceProps) {
  const [input, setInput] = useState("");
  const [mode, setMode] = useState(tool.modes[0]?.id ?? "default");
  const [sourceLanguage, setSourceLanguage] = useState("auto");
  const [targetLanguage, setTargetLanguage] = useState<Locale | "same">(locale);
  const [scenario, setScenario] = useState("general");
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [usageCount, setUsageCount] = useState(0);
  const [result, setResult] = useState<AiTransformResponse | null>(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasCopied, setHasCopied] = useState(false);
  const metrics = useMemo(() => analyzeText(input), [input]);
  const copy = getWorkspaceCopy(locale);
  const remainingAiActions = Math.max(DAILY_AI_LIMIT - usageCount, 0);
  const selectedModeLabel = tool.modes.find((toolMode) => toolMode.id === mode)?.label ?? mode;
  const selectedSourceLabel =
    sourceLanguage === "auto" ? (copy.autoDetect as string) : locales.find((candidate) => candidate.languageName === sourceLanguage)?.nativeName ?? sourceLanguage;
  const selectedTargetLabel =
    targetLanguage === "same" ? (copy.keepLanguage as string) : locales.find((candidate) => candidate.id === targetLanguage)?.nativeName ?? String(targetLanguage);
  const shouldShowResultColumn = Boolean(error || result || historyItems.length > 0);

  useEffect(() => {
    setHistoryItems(readStoredHistory());
    setUsageCount(readUsageCount());
  }, []);

  function saveHistory(response: AiTransformResponse) {
    const item: HistoryItem = {
      id: createId(),
      toolId: tool.id,
      toolName: tool.name,
      mode,
      sourceLanguage,
      targetLanguage,
      scenario,
      input,
      output: response.output,
      createdAt: new Date().toISOString()
    };
    const nextItems = [item, ...historyItems].slice(0, 8);
    setHistoryItems(nextItems);
    try {
      window.localStorage.setItem(HISTORY_KEY, JSON.stringify(nextItems));
    } catch {
      // Local history is a convenience feature; failing storage should not block editing.
    }
  }

  function incrementUsage() {
    const nextCount = usageCount + 1;
    setUsageCount(nextCount);
    try {
      window.localStorage.setItem(todayUsageKey(), String(nextCount));
    } catch {
      // Ignore local storage quota or privacy-mode failures.
    }
  }

  function handleScenarioChange(nextScenario: string) {
    setScenario(nextScenario);
    const selectedTemplate = scenarioTemplates.find((template) => template.id === nextScenario);
    if (selectedTemplate && !input.trim()) {
      setInput(selectedTemplate.sample);
    }
  }

  async function handleFileUpload(file: File | undefined) {
    if (!file) {
      return;
    }

    setError("");
    setUploadedFileName(file.name);

    try {
      const text = await file.text();
      if (text.length > MAX_IMPORTED_CHARS) {
        setInput(text.slice(0, MAX_IMPORTED_CHARS));
        setError((copy.importTooLong as string).replace("{limit}", String(MAX_IMPORTED_CHARS)));
        return;
      }
      setInput(text);
    } catch {
      setError(copy.importFailed as string);
    }
  }

  function restoreHistory(item: HistoryItem) {
    setInput(item.input);
    setMode(item.mode);
    setSourceLanguage(item.sourceLanguage);
    setTargetLanguage(item.targetLanguage as Locale | "same");
    setScenario(item.scenario);
    setResult({
      output: item.output,
      highlights: [(copy.restoredFromHistory as string).replace("{tool}", item.toolName)],
      warnings: []
    });
  }

  async function runTool() {
    setError("");
    setResult(null);

    if (!input.trim()) {
      setError(copy.missingText as string);
      return;
    }

    if (tool.id === "word-counter") {
      const plural = metrics.readingTimeMinutes === 1 ? "" : "s";
      const localResult = {
        output: (copy.wordCounterOutput as string)
          .replace("{words}", String(metrics.words))
          .replace("{characters}", String(metrics.characters))
          .replace("{sentences}", String(metrics.sentences))
          .replace("{minutes}", String(metrics.readingTimeMinutes))
          .replace("{plural}", plural),
        highlights: [
          `${copy.readability}: ${metrics.readabilityLabel}`,
          `${copy.averageSentence}: ${metrics.averageWordsPerSentence} ${copy.words}`
        ],
        warnings: []
      };
      setResult(localResult);
      saveHistory(localResult);
      return;
    }

    if (usageCount >= DAILY_AI_LIMIT) {
      setError((copy.dailyLimitReached as string).replace("{limit}", String(DAILY_AI_LIMIT)));
      return;
    }

    setIsLoading(true);
    try {
      const response = await transformText({
        tool: tool.id,
        input,
        options: {
          mode,
          sourceLanguage,
          scenario,
          targetLanguage: targetLanguage === "same" ? getLocale(locale).languageName : getLocale(targetLanguage as Locale).languageName
        }
      });
      setResult(response);
      incrementUsage();
      saveHistory(response);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : (copy.unavailable as string));
    } finally {
      setIsLoading(false);
    }
  }

  async function copyResult() {
    if (!result?.output) {
      return;
    }

    await navigator.clipboard.writeText(result.output);
    setHasCopied(true);
    window.setTimeout(() => setHasCopied(false), 1600);
  }

  return (
    <section className={shouldShowResultColumn ? "workspace" : "workspace workspace--single"} aria-label={`${tool.name} workspace`}>
      <div className="workspace__input">
        <div className="workspace__input-header">
          <label htmlFor="tool-input">{tool.inputLabel}</label>
        </div>
        <textarea
          id="tool-input"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder={tool.inputPlaceholder}
          rows={14}
        />
        <div className="metric-strip metric-strip--input">
          <span>
            <strong>{metrics.words}</strong> {copy.words}
          </span>
          <span>
            <strong>{metrics.characters}</strong> {copy.chars}
          </span>
          <span>
            <strong>{metrics.sentences}</strong> {copy.sentences}
          </span>
          <span>
            <strong>{metrics.readingTimeMinutes}</strong> {copy.minRead}
          </span>
        </div>
        <div className="workspace__primary-action">
          <button type="button" className="primary-button primary-button--wide" onClick={runTool} disabled={isLoading}>
            <Sparkles size={17} aria-hidden="true" />
            {isLoading ? copy.working : tool.primaryAction}
          </button>
        </div>
        {uploadedFileName && <p className="workspace-note">{(copy.uploadedFile as string).replace("{name}", uploadedFileName)}</p>}
        <details className="workspace-settings">
          <summary>
            <span>{copy.settingsTitle}</span>
            <strong>
              {selectedModeLabel} · {selectedSourceLabel} → {selectedTargetLabel}
            </strong>
          </summary>
          <div className="workspace__quick-actions" aria-label={copy.inputActionsLabel as string}>
            <button type="button" className="text-button" onClick={() => setInput(SAMPLE_TEXT)}>
              {copy.sample}
            </button>
            <label className="text-button file-button" htmlFor="document-upload">
              {copy.uploadDocument}
            </label>
            <input
              className="sr-only"
              id="document-upload"
              type="file"
              accept=".txt,.md,.csv,.json,.html,.rtf"
              onChange={(event) => void handleFileUpload(event.target.files?.[0])}
            />
            {input.trim() && (
              <button type="button" className="text-button" onClick={() => setInput("")}>
                {copy.clear}
              </button>
            )}
          </div>
          <div className="workspace-settings__grid">
            <label className="select-label">
              <span>{copy.scenarioLabel}</span>
              <select value={scenario} onChange={(event) => handleScenarioChange(event.target.value)} aria-label={copy.scenarioLabel as string}>
                {scenarioTemplates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {copy[template.labelKey] as string}
                  </option>
                ))}
              </select>
            </label>
            <label className="select-label">
              <span>{copy.modeLabel}</span>
              <select value={mode} onChange={(event) => setMode(event.target.value)} aria-label={copy.modeLabel as string}>
                {tool.modes.map((toolMode) => (
                  <option key={toolMode.id} value={toolMode.id}>
                    {toolMode.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="select-label">
              <span>{copy.sourceLanguageLabel}</span>
              <select value={sourceLanguage} onChange={(event) => setSourceLanguage(event.target.value)} aria-label={copy.sourceLanguageLabel as string}>
                <option value="auto">{copy.autoDetect}</option>
                {locales.map((candidate) => (
                  <option key={candidate.id} value={candidate.languageName}>
                    {candidate.nativeName}
                  </option>
                ))}
              </select>
            </label>
            <label className="select-label">
              <span>{copy.targetLanguageLabel}</span>
              <select
                value={targetLanguage}
                onChange={(event) => setTargetLanguage(event.target.value as Locale | "same")}
                aria-label={copy.targetLanguageLabel as string}
              >
                <option value="same">{copy.keepLanguage}</option>
                {locales.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.nativeName}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </details>
      </div>

      {shouldShowResultColumn && (
        <div className="workspace__result" aria-live="polite">
          {error && <p className="error-message">{error}</p>}
          {result && (
            <div className="result-panel">
              <div className="result-panel__header">
                <h2>{copy.resultTitle}</h2>
                <button type="button" className="secondary-button" onClick={copyResult}>
                  <Copy size={16} aria-hidden="true" />
                  {hasCopied ? copy.copied : copy.copy}
                </button>
              </div>
              <p>{result.output}</p>
              {result.highlights.length > 0 && (
                <ul>
                  {result.highlights.map((highlight) => (
                    <li key={highlight}>{highlight}</li>
                  ))}
                </ul>
              )}
              {result.warnings.map((warning) => (
                <p className="warning-message" key={warning}>
                  {warning}
                </p>
              ))}
              <div className="comparison-panel">
                <strong>{copy.benchmarkTitle}</strong>
                <p>{copy.benchmarkBody}</p>
              </div>
              <div className="quality-checklist">
                <strong>{copy.qualityTitle}</strong>
                <ul>
                  {(copy.qualityItems as string[]).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          {historyItems.length > 0 && (
            <details className="history-panel">
              <summary className="history-panel__header">
                <strong>{copy.historyTitle}</strong>
              </summary>
              <div className="history-list">
                {historyItems.slice(0, 5).map((item) => (
                  <button type="button" key={item.id} className="history-item" onClick={() => restoreHistory(item)}>
                    <span>
                      <strong>{item.toolName}</strong>
                      {new Date(item.createdAt).toLocaleDateString()}
                    </span>
                    <RotateCcw size={15} aria-hidden="true" />
                  </button>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </section>
  );
}
